import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import DualScoreVisualization from "./DualScoreVisualization";

import PlatformCard from "./PlatformCard";
import ActionPlan from "./ActionPlan";
import { useTechnicalChecks } from "@/hooks/useTechnicalChecks";
import DetailedMethodology from "./DetailedMethodology";
import EvidenceBox from "./EvidenceBox";

import ResultsFAQ from "./ResultsFAQ";
import Confetti from "./Confetti";

import ExecutiveSummary from "./results/ExecutiveSummary";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, CheckCircle2, XCircle, ExternalLink, HelpCircle, Brain, TrendingUp, Lock, GraduationCap, Wrench, AlertTriangle } from "lucide-react";
import { ResponsiveTooltip } from "@/components/ui/responsive-tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { VisibilityAnalysis } from "@/lib/api/ai-visibility";
import type { PlatformResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";
import { trackScanCompleted, trackPageView } from "@/lib/analytics";
import type { ScanStatus } from "@/hooks/useScanProgress";
import { calculateVisibilityScore } from "@/lib/scoring";

interface ScanResultsProps {
  analysis: VisibilityAnalysis;
  brandName: string;
  keyword?: string;
  platformResults?: PlatformResult[];
  googleResult?: GoogleSearchResult | null;
  websiteUrl?: string;
  chatGptStatus?: ScanStatus;
  googleStatus?: ScanStatus;
  isFromCache?: boolean;
  // V2.1 Discovery Mode fields
  aiScore?: number;
  queryType?: 'discovery' | 'fallback' | 'not_found' | 'error';
  queryUsed?: string;
  rawResponseSnippet?: string;
  competitorMentions?: string[];
}

const ScanResults = ({ 
  analysis, 
  brandName, 
  keyword, 
  platformResults, 
  googleResult, 
  websiteUrl, 
  chatGptStatus = "success", 
  googleStatus = "success", 
  isFromCache = false,
  // V2.1 Discovery Mode fields
  aiScore,
  queryType,
  queryUsed,
  rawResponseSnippet,
  competitorMentions,
}: ScanResultsProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [showAIPlatformsModal, setShowAIPlatformsModal] = useState(false);
  const { toast } = useToast();
  const techState = useTechnicalChecks(websiteUrl);

  // Get AI Platforms result from API if available (previously ChatGPT)
  const aiPlatformsResult = platformResults?.find(p => p.platform === "ChatGPT") || {
    platform: "AI Platforms",
    found: false,
    context: "Not scanned",
    confidence: "low" as const,
  };

  // Get Google Search result
  const googleSearchResult = googleResult || {
    platform: "Google Search",
    found: false,
    context: "Not scanned",
    confidence: "low" as const,
    position: null as number | null,
    spellingCorrected: false,
    suggestedQuery: undefined as string | undefined,
  };

  // Calculate comprehensive score using new algorithm
  const scoring = calculateVisibilityScore({
    aiScore: aiScore, // V2.1: Pass explicit aiScore from Discovery Mode
    aiPlatformsFound: aiPlatformsResult.found,
    googleFound: googleSearchResult.found,
    googlePosition: googleSearchResult.position || null,
    googleConfidence: googleSearchResult.confidence,
    isPaidUser: false, // Automated scan - max 70/100
  });

  const weightedScore = scoring.currentScore;

  // Trigger confetti

  // Trigger confetti for high scores, track scan completed, and update URL for analytics
  useEffect(() => {
    // Update URL to /results for analytics separation (shallow routing)
    // Skip if already on a /scan/:id shareable URL
    if (window.history && window.location.pathname !== '/results' && !window.location.pathname.startsWith('/scan/')) {
      window.history.pushState({ results: true }, '', '/results');
      // Trigger manual page view for GA4 and Clarity
      trackPageView('/results', `AI Visibility Results - ${brandName}`);
    }

    // Track scan completed event
    trackScanCompleted(
      brandName,
      keyword || '',
      weightedScore,
      aiPlatformsResult.found,
      googleSearchResult.found,
      websiteUrl
    );
    
    // Set flag that user completed a scan (for methodology page protection)
    sessionStorage.setItem('ailens_scan_completed', 'true');
    sessionStorage.setItem('ailens_scan_timestamp', Date.now().toString());
    
    // Confetti only for truly exceptional scores (which won't happen on automated scan due to max 70)
    // But keep the logic in case extended analysis is added later
    if (scoring.currentScore >= 90) {
      setTimeout(() => setShowConfetti(true), 1500);
    }
  }, [brandName, keyword, weightedScore, aiPlatformsResult.found, googleSearchResult.found]);


  // Scoring Methodology Modal - Updated for V2.1
  const ScoringMethodologyModal = () => (
    <Dialog open={showScoringModal} onOpenChange={setShowScoringModal}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            How Your Score is Calculated
          </DialogTitle>
          <DialogDescription>
            Transparent, research-backed methodology
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 text-sm">
          {/* Formula */}
          <section className="p-4 bg-muted/50 border">
            <h4 className="font-semibold mb-3 text-center">The Formula</h4>
            <div className="text-xl font-mono text-center mb-4">
              Score = AI (40) + Google (30) + Additional Platforms (30)
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Points earned based on platform visibility
            </p>
          </section>

          {/* Components */}
          <section>
            <h4 className="font-semibold mb-3">Score Components</h4>
            <div className="space-y-3">
              <div className="p-3 border bg-muted/30 space-y-3">
                <div className="flex items-start gap-3">
                  <Brain className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">ChatGPT Visibility (40 pts)</div>
                      <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded">✓ Scanned</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Does AI <strong>recommend</strong> your brand? Discovery mode tests real recommendations.
                    </p>
                  </div>
                </div>
                
                {/* AI Discovery Levels breakdown */}
                <div className="space-y-1.5 pl-8">
                  <p className="text-xs font-medium text-foreground">AI Discovery Levels:</p>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 40 ? "bg-success/15 border border-success/30 font-medium" : ""
                  )}>
                    <span className={aiScore === 40 ? "text-foreground" : "text-muted-foreground"}>Recommended in discovery queries</span>
                    <span className={cn("font-mono", aiScore === 40 ? "text-success" : "text-muted-foreground")}>+40 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 20 ? "bg-warning/15 border border-warning/30 font-medium" : ""
                  )}>
                    <span className={aiScore === 20 ? "text-foreground" : "text-muted-foreground"}>Recognized, not yet recommended</span>
                    <span className={cn("font-mono", aiScore === 20 ? "text-warning" : "text-muted-foreground")}>+20 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 0 ? "bg-muted/50 border border-border font-medium" : ""
                  )}>
                    <span className={aiScore === 0 ? "text-foreground" : "text-muted-foreground"}>Not found by AI</span>
                    <span className="font-mono text-muted-foreground">0 points</span>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground pl-8 leading-relaxed">
                  We test two things: Does AI recommend your brand when users search for your category? And does AI recognize your brand when asked directly? Being recognized but not recommended is common, and it's your biggest optimization opportunity.
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 border bg-muted/30">
                <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">Google Search (30 pts)</div>
                    <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded">✓ Scanned</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Top 3 = 30pts, Top 10 = 20pts, Top 20 = 10pts. Higher positions yield more AI citations.
                    <a href="https://arxiv.org/abs/2507.05301" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">(Yang, AAAI 2025)</a>
                  </p>
                </div>
              </div>
              <div className="w-full flex items-start gap-3 p-3 border border-dashed bg-muted/50 opacity-70">
                <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-muted-foreground">Additional Platforms (30 pts)</div>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex items-center gap-1"><Lock className="w-3 h-3" /> Not included</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Additional AI platforms not included in the automated scan.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Google Position Rewards - V2.1 */}
          <section>
            <h4 className="font-semibold mb-3">Google Position Rewards</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Position 1-3 (AI citation zone)</span>
                <span className="font-mono text-accent">+30 points</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Position 4-10 (Page 1)</span>
                <span className="font-mono text-warning">+20 points</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Position 11-20</span>
                <span className="font-mono text-warning">+10 points</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Position 21+ or not found</span>
                <span className="font-mono text-muted-foreground">0 points</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Top 20 domains capture 67% of all AI citations. Higher positions = more AI visibility.
              <a href="https://arxiv.org/abs/2507.05301" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">(Yang, AAAI 2025)</a>
            </p>
          </section>

          {/* Research Note */}
          <section className="p-3 bg-primary/5 border border-primary/20">
            <h4 className="font-semibold mb-2 text-sm">Research-Backed Methodology</h4>
            <p className="text-xs text-muted-foreground">
              Our scoring is based on peer-reviewed research from AAAI, NeurIPS, and arXiv. 
              No penalties: only rewards for strong positions.
            </p>
          </section>

          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              Want to learn more about our methodology?
            </p>
            <a 
              href="/methodology"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
            >
              View Full Methodology
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // AI Platforms Modal
  const AIPlatformsModal = () => (
    <Dialog open={showAIPlatformsModal} onOpenChange={setShowAIPlatformsModal}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            AI Platforms Visibility
          </DialogTitle>
          <DialogDescription>
            How we check your brand presence across AI chatbots
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 text-sm">
          <section>
            <h4 className="font-semibold text-base mb-2">What We Check</h4>
            <p className="text-muted-foreground">
              We analyze if your brand appears when AI platforms generate responses 
              to queries related to your industry and keywords.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Platforms Analyzed</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 border bg-muted/30">
                <div className="font-medium">ChatGPT</div>
                <div className="ml-auto">
                  <span className="text-xs bg-success/10 text-success px-2 py-1 border border-success/20">
                    Active
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 border bg-muted/30">
                <div>
                  <div className="font-medium">Additional AI Platforms</div>
                   <p className="text-xs text-muted-foreground mt-1">
                    Not included in the automated scan
                  </p>
                </div>
                <div className="ml-auto">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Why It Matters</h4>
            <p className="text-muted-foreground">
              AI platforms are becoming primary discovery channels. When someone asks 
              ChatGPT for recommendations in your space, you want your brand to appear 
              in the response. This directly impacts brand awareness and consideration.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Scoring Weight</h4>
            <p className="text-muted-foreground">
              ChatGPT visibility accounts for <strong>40% of your total score</strong>. 
              Combined with Google Search (30%) and additional platforms (30%), this creates 
              a complete picture of your AI visibility.
            </p>
          </section>

          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              Want to learn more about our methodology?
            </p>
            <a 
              href="/methodology"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
            >
              View Full Methodology
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Google Search Visibility Modal
  const GoogleVisibilityModal = () => (
    <Dialog open={showGoogleModal} onOpenChange={setShowGoogleModal}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Google Search Visibility</DialogTitle>
          <DialogDescription>
            How we check your Google ranking and why it matters
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">What We Check</h4>
            <p className="text-sm text-muted-foreground">
              We search Google for your keyword (e.g., "{keyword || 'your keyword'}") and verify if YOUR domain appears 
              in the top 10 organic results. This is a generic search without your brand name, measuring true competitive visibility.
            </p>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Results Meaning</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-success">Found:</span> Your domain 
                  appears in Google's top 10 results
                </p>
              </div>
              <div className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-destructive">Not Found:</span> Either 
                  you're not ranking yet, or Google doesn't recognize your brand 
                  name (may autocorrect it)
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Why It Matters</h4>
            <p className="text-sm text-muted-foreground">
              Academic research confirms AI platforms use different selection criteria than 
              Google. While ~60% of AI citations overlap with top Google results, 
              <span className="font-medium text-foreground"> ~40% come from sources outside the top 10</span>, making separate 
              AI visibility tracking essential.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Source: <a href="https://arxiv.org/abs/2510.11560" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Kirsten et al., 2025</a>
            </p>
          </div>

          <div className="h-px bg-border" />

          {/* Research Foundation */}
          <div className="mt-6 space-y-3">
            <p className="text-sm font-medium">Research Foundation</p>
            
            {/* Source 1: Yang AAAI 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href="https://arxiv.org/abs/2507.05301"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  Yang (AAAI 2025) <span className="text-success">✓</span>
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  Top 20 domains capture 67% of all AI citations
                </p>
              </div>
            </div>

            {/* Source 2: Kirsten et al. 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href="https://arxiv.org/abs/2510.11560"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  Kirsten et al. (arXiv 2025)
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  ~40% of AI-cited sources don't appear in Google's top organic results
                </p>
              </div>
            </div>

            {/* Source 2: Semrush July 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href="https://www.semrush.com/blog/ai-mode-comparison-study/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  Semrush (July 2025)
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  Research shows significant overlap between AI platform citations and Google's top results
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Want to learn more about our methodology?{" "}
              <a href="/methodology" className="text-primary hover:underline">
                View Full Methodology →
              </a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Confetti trigger={showConfetti} />
      
      {/* Modals */}
      <ScoringMethodologyModal />
      <AIPlatformsModal />
      <GoogleVisibilityModal />

      <div className="w-full max-w-full md:max-w-5xl mx-auto px-4 md:px-6">
        <Card className="w-full bg-card border-2 border-border rounded-none shadow-sm">
          <CardContent className="pt-8 md:pt-12 px-5 md:px-8 space-y-6 md:space-y-8">
            {/* Score Section with localized glow */}
            <div className="relative results-entrance" style={{ "--entrance-delay": "0ms" } as React.CSSProperties}>
              <div className="hero-glow absolute inset-0 pointer-events-none -m-8" aria-hidden="true" />
              <div className="relative">
                <DualScoreVisualization 
                  scoring={scoring}
                  brandName={brandName}
                  websiteUrl={websiteUrl}
                  skipAnimation={isFromCache}
                  isLoading={chatGptStatus === "loading" || googleStatus === "loading"}
                />
              </div>
            </div>

            {/* Executive Summary */}
            {aiScore !== undefined && (
              <div className="results-entrance" style={{ "--entrance-delay": "50ms" } as React.CSSProperties}>
                <ExecutiveSummary
                  aiScore={aiScore}
                  totalScore={scoring.currentScore}
                  brandName={brandName}
                  keyword={keyword || ''}
                  competitorMentions={competitorMentions}
                  googlePosition={googleSearchResult.position || null}
                  queryUsed={queryUsed || ''}
                />
              </div>
            )}

            {/* Inline AI score context + How is this calculated — AFTER Pro CTA */}
            <div className="results-entrance flex flex-col items-center gap-2 -mt-2" style={{ "--entrance-delay": "120ms" } as React.CSSProperties}>
              {/* Inline ChatGPT score message */}
              {aiScore !== undefined && (
                <p className="text-sm text-center max-w-md">
                  {aiScore === 40 && (
                    <>
                      <span className="font-medium text-success">ChatGPT: 40/40</span>
                      <span className="text-muted-foreground"> : Recommended. AI actively suggests your brand for relevant queries.</span>
                    </>
                  )}
                  {aiScore === 20 && (
                    <>
                      <span className="font-medium text-warning">ChatGPT: 20/40</span>
                      <span className="text-muted-foreground"> : Recognized, not yet recommended. AI knows your brand but doesn't suggest it in discovery queries yet.</span>
                    </>
                  )}
                  {aiScore === 0 && (
                    <>
                      <span className="font-medium text-destructive">ChatGPT: 0/40</span>
                      <span className="text-muted-foreground"> : Not found. AI doesn't recognize your brand yet.</span>
                    </>
                  )}
                </p>
              )}
              <button
                onClick={() => setShowScoringModal(true)}
                className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 transition-all cursor-pointer flex items-center gap-1 mx-auto"
              >
                <HelpCircle className="h-4 w-4" />
                How is this calculated?
              </button>

            </div>

            {/* V2.1 Evidence Box */}
            {queryType && queryUsed && rawResponseSnippet && (
              <div className="results-entrance" style={{ "--entrance-delay": "200ms" } as React.CSSProperties}>
                <EvidenceBox
                  queryUsed={queryUsed}
                  rawResponseSnippet={rawResponseSnippet}
                  brandName={brandName}
                  queryType={queryType}
                  aiScore={aiScore}
                  competitorMentions={competitorMentions}
                />
              </div>
            )}

            {/* Platform Results Header with Academic Tooltip */}
            <div className="results-entrance flex items-center gap-2" style={{ "--entrance-delay": "300ms" } as React.CSSProperties}>
              <h3 className="text-lg font-semibold text-foreground">Platform Analysis</h3>
              <ResponsiveTooltip
                content={
                  <div className="max-w-xs space-y-1">
                    <p className="text-sm font-medium">Top 20 domains capture 67% of all AI citations</p>
                    <p className="text-xs text-muted-foreground">Source: Yang, AAAI 2025</p>
                  </div>
                }
                side="top"
              >
                <GraduationCap className="h-4 w-4 text-primary/70 hover:text-primary transition-colors cursor-help" />
              </ResponsiveTooltip>
            </div>

            {/* Platform Results */}
            <div className="results-entrance grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" style={{ "--entrance-delay": "400ms" } as React.CSSProperties}>
              <div>
                <PlatformCard 
                  name="AI Platforms" 
                  found={aiPlatformsResult.found}
                  context={aiPlatformsResult.context}
                  confidence={aiPlatformsResult.confidence}
                  status={chatGptStatus}
                  skipAnimation={isFromCache}
                  infoTooltip="Automated scan: ChatGPT visibility. Extended analysis covers additional AI platforms."
                  onLearnMore={() => setShowAIPlatformsModal(true)}
                />
              </div>
              <div>
                <PlatformCard
                  name="Google Search" 
                  found={googleSearchResult.found}
                  context={googleSearchResult.context}
                  confidence={googleSearchResult.confidence}
                  status={googleStatus}
                  skipAnimation={isFromCache}
                  infoTooltip="SEO yields 7× more visibility than GEO-only (Puerto et al., NeurIPS 2025)"
                  spellingCorrected={googleSearchResult.spellingCorrected}
                  suggestedQuery={googleSearchResult.suggestedQuery}
                  onLearnMore={() => setShowGoogleModal(true)}
                />
              </div>
            </div>

            {/* AI-Readiness Action Plan */}
            <div className="results-entrance" style={{ "--entrance-delay": "500ms" } as React.CSSProperties}>
              <ActionPlan websiteUrl={websiteUrl} techStatus={techState.status} techData={techState.data} />
            </div>

            {/* Detailed Methodology */}
            <div className="results-entrance" style={{ "--entrance-delay": "600ms" } as React.CSSProperties}>
              <DetailedMethodology />
            </div>

            {/* FAQ Section */}
            <div className="results-entrance" style={{ "--entrance-delay": "650ms" } as React.CSSProperties}>
              <ResultsFAQ />
            </div>

          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ScanResults;
